import axios from "axios";
import { queryClient } from "@/lib/queryClient";

// Empty REACT_APP_BACKEND_URL → same-origin `/api` (nginx proxies to backend).
// Required for HttpOnly SameSite=Lax cookies when FE/BE share one public host.
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

const PUBLIC_TTLS = Object.freeze({
  config: 5 * 60 * 1000,
  leaderboard: 5 * 1000,
  stats: 5 * 1000,
  activity: 5 * 1000,
  pending: 5 * 1000,
  backers: 30 * 1000,
});

function publicQuery(key, queryFn, staleTime) {
  return queryClient.fetchQuery({
    queryKey: ["public", ...key],
    queryFn,
    staleTime,
  });
}

// Realtime updates call this before reloading affected public data. The
// partial keys invalidate every range/category variant without touching auth.
export function invalidatePublicData(key) {
  return queryClient.invalidateQueries({ queryKey: ["public", ...key] });
}

export function wsUrl() {
  const base = BACKEND_URL.replace(/^http/, "ws");
  return `${base}/api/ws`;
}

// ---- API calls ----
export const getConfig = () => publicQuery(
  ["config"],
  () => api.get("/config").then((r) => r.data),
  PUBLIC_TTLS.config,
);
export const getLeaderboard = (category, range = "all", chain = null, filters = {}) => {
  const classification = {
    asset_class: filters.asset_class || undefined,
    asset_subtype: filters.asset_subtype || undefined,
    sector_tag: filters.sector_tag || undefined,
    classification_status: filters.classification_status || undefined,
  };
  return publicQuery(
    ["leaderboard", category, range, chain || "", JSON.stringify(classification)],
    () => api.get(`/leaderboard`, {
      params: {
        category,
        range,
        ...(chain ? { chain } : {}),
        ...Object.fromEntries(
          Object.entries(classification).filter(([, v]) => v != null && v !== ""),
        ),
      },
    }).then((r) => r.data),
    PUBLIC_TTLS.leaderboard,
  );
};

export const getSeasons = () =>
  publicQuery(
    ["seasons"],
    () => api.get("/seasons").then((r) => r.data),
    30 * 1000,
  );

export const getSeason = (slug) =>
  api.get(`/seasons/${slug}`).then((r) => r.data);

export const getSeasonLeaderboard = (slug, params = {}) =>
  api.get(`/seasons/${slug}/leaderboard`, { params }).then((r) => r.data);
export const getEntity = (slug) => api.get(`/entity/${slug}`).then((r) => r.data);
export const getEntityContributions = (slug) =>
  api.get(`/entity/${slug}/contributions`).then((r) => r.data);
export const getEntitySupporters = (slug) =>
  api.get(`/entity/${slug}/supporters`).then((r) => r.data);
export const getActivity = () => publicQuery(
  ["activity"],
  () => api.get("/activity").then((r) => r.data),
  PUBLIC_TTLS.activity,
);
export const getStats = () => publicQuery(
  ["stats"],
  () => api.get("/stats").then((r) => r.data),
  PUBLIC_TTLS.stats,
);
export const getEntityFlips = (slug, limit = 20) =>
  api.get(`/entity/${slug}/flips`, { params: { limit } }).then((r) => r.data);
export const getBackers = (range = "all") =>
  publicQuery(
    ["backers", range],
    () => api.get("/backers", { params: { range } }).then((r) => r.data),
    PUBLIC_TTLS.backers,
  );
export const getUser = (wallet) => api.get(`/user/${wallet}`).then((r) => r.data);
export const search = (q) => api.get("/search", { params: { q } }).then((r) => r.data);
export const getPending = (category = "tokens") =>
  publicQuery(
    ["pending", category],
    () => api.get("/pending", { params: { category } }).then((r) => r.data),
    PUBLIC_TTLS.pending,
  );
export const adminGetSettings = () => api.get("/admin/settings").then((r) => r.data);
export const adminPutSettings = (payload) => api.put("/admin/settings", payload).then((r) => r.data);
export const pricePreview = (symbol, amount) =>
  api.get("/prices/preview", { params: { symbol, amount } }).then((r) => r.data);
export const tokenLivePrice = (entity_id) =>
  api.get("/prices/token", { params: { entity_id } }).then((r) => r.data);
export const getSolanaMintDecimals = (mint) =>
  api.get(`/solana/mint/${mint}`).then((r) => r.data);
export const getSolanaBlockhash = () =>
  api.get("/solana/blockhash").then((r) => r.data);
export const apiSolBalance = (address) =>
  api.get(`/solana/balance/${address}`).then((r) => r.data);
export const apiSolTokenBalance = (address, mint) =>
  api.get("/solana/token-balance", { params: { address, mint } }).then((r) => r.data);
export const getPortfolioBalance = (address, ecosystem = "evm") =>
  api.get(`/portfolio/${address}`, { params: { ecosystem } }).then((r) => r.data);
export const createPaymentIntent = (payload) =>
  api.post("/payment-intent", payload).then((r) => r.data);
export const getPaymentIntent = (id) =>
  api.get(`/payment-intent/${id}`).then((r) => r.data);
export const getUserIntents = (wallet) =>
  api.get(`/user/${wallet}/intents`).then((r) => r.data);
export const claimStart = (entityId) =>
  api.post(`/entity/${entityId}/claim/start`).then((r) => r.data);
export const claimVerify = (entityId, tweet_url) =>
  api.post(`/entity/${entityId}/claim/verify`, { tweet_url }).then((r) => r.data);
export const submitAssetClassification = (entityId, payload) =>
  api.put(`/entity/${entityId}/classification`, payload).then((r) => r.data);
export const updateEntityProfile = (entityId, payload) =>
  api.put(`/entity/${entityId}/profile`, payload).then((r) => r.data);
export const linkWallet = (entityId, edit_token, address, signature) =>
  api.post(`/entity/${entityId}/link-wallet`, { edit_token, address, signature }).then((r) => r.data);
export const authWallet = (entityId, address, signature) =>
  api.post(`/entity/${entityId}/auth-wallet`, { address, signature }).then((r) => r.data);
export const setEntityWebhook = (entityId, editToken, url) =>
  api.put(`/entity/${entityId}/webhook`, { edit_token: editToken, url }).then((r) => r.data);
export const uploadEntityAvatar = (entityId, file, editToken) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("edit_token", editToken);
  return api.post(`/entity/${entityId}/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const avatarFromX = (entityId, editToken) =>
  api.post(`/entity/${entityId}/avatar-from-x`, { edit_token: editToken }).then((r) => r.data);
export const createEntity = (payload) => api.post("/entity", payload).then((r) => r.data);
export const tokenLookup = (address, chain) =>
  api.get("/token/lookup", { params: { address, chain } }).then((r) => r.data);
export const contribute = (payload) => api.post("/contribute", payload).then((r) => r.data);
export const contributeOnchain = (payload) => api.post("/contribute/onchain", payload).then((r) => r.data);

// admin
export const adminLogin = (email, password) =>
  api.post("/auth/login", { email, password }).then((r) => r.data);
export const adminMe = () => api.get("/auth/me").then((r) => r.data);
export const adminLogout = () => api.post("/auth/logout").then((r) => r.data);
export const adminStats = () => api.get("/admin/stats").then((r) => r.data);
export const adminEntities = () => api.get("/admin/entities").then((r) => r.data);
export const adminContributions = () =>
  api.get("/admin/contributions").then((r) => r.data);
export const adminPause = (id) => api.post(`/admin/entity/${id}/pause`).then((r) => r.data);
export const adminDelete = (id) => api.delete(`/admin/entity/${id}`).then((r) => r.data);
export const adminVerifyClaim = (id) =>
  api.post(`/admin/entity/${id}/verify-claim`).then((r) => r.data);
export const adminVerifyTx = (payload) => api.post("/verify-tx", payload).then((r) => r.data);
export const adminHypeTokens = (refresh = false) =>
  api.get("/admin/hype/tokens", { params: refresh ? { refresh: true } : {} }).then((r) => r.data);
export const adminHypeKols = (handles) =>
  api.get("/admin/hype/kols", { params: handles ? { handles } : {} }).then((r) => r.data);
/** Hype Radar V2 — ecosystem score + optional External Intelligence evidence (admin). */
export const adminHypeV2 = (params = {}) =>
  api.get("/admin/hype/v2", { params }).then((r) => r.data);
export const adminHypeV2Narratives = (limit = 25) =>
  api.get("/admin/hype/v2/narratives", { params: { limit } }).then((r) => r.data);
export const adminHypeV2NarrativeDetail = (key, params = {}) =>
  api.get(`/admin/hype/v2/narratives/${encodeURIComponent(key)}`, { params }).then((r) => r.data);
export const adminHypeV2TokenDetail = (key, params = {}) =>
  api.get(`/admin/hype/v2/tokens/${encodeURIComponent(key)}`, { params }).then((r) => r.data);
export const adminHypeV2CreateAlert = (payload) =>
  api.post("/admin/hype/v2/alerts", payload).then((r) => r.data);

export const getBattles = () =>
  publicQuery(
    ["battles"],
    () => api.get("/battles").then((r) => r.data),
    15 * 1000,
  );
export const getBattle = (slug) =>
  api.get(`/battles/${slug}`).then((r) => r.data);
export const getBattleEvents = (slug, params = {}) =>
  api.get(`/battles/${slug}/events`, { params }).then((r) => r.data);
export const getBattleRewards = (slug) =>
  api.get(`/battles/${slug}/rewards`).then((r) => r.data);
export const getRewardPool = (idOrSlug) =>
  api.get(`/reward-pools/${idOrSlug}`).then((r) => r.data);
export const getMyRewards = (params = {}) =>
  api.get("/rewards", { params }).then((r) => r.data);
export const claimReward = (id) =>
  api.post(`/rewards/${id}/claim`).then((r) => r.data);
export const getProjectWarRoom = (entityId) =>
  api.get(`/project/${entityId}/war-room`).then((r) => r.data);
export const createProjectBattle = (payload) =>
  api.post("/battles", payload).then((r) => r.data);
export const trackBattleAnalytics = (slug, eventName) => {
  if (!slug || !eventName) return Promise.resolve(null);
  return api.post(`/battles/${slug}/analytics/track`, { event_name: eventName }).then((r) => r.data);
};
export const getBattleAnalytics = (slug) =>
  api.get(`/battles/${slug}/analytics`).then((r) => r.data);
export const shareCardUrl = (eventId) =>
  `${api.defaults.baseURL}/competition/events/${eventId}/share-card`;
export const getNotifications = (params = {}) =>
  api.get("/notifications", { params }).then((r) => r.data);
export const markNotificationRead = (id) =>
  api.post(`/notifications/${id}/read`).then((r) => r.data);
export const markAllNotificationsRead = () =>
  api.post("/notifications/read-all").then((r) => r.data);
export const getCompetitionEvents = (params = {}) =>
  api.get("/competition/events", { params }).then((r) => r.data);
export const getSeasonEvents = (slug, params = {}) =>
  api.get(`/seasons/${slug}/events`, { params }).then((r) => r.data);


export const getSettlementGates = () =>
  api.get("/settlement/gates").then((r) => r.data);
export const adminSettlementDashboard = () =>
  api.get("/admin/settlement/dashboard").then((r) => r.data);
export const adminPauseSettlement = () =>
  api.post("/admin/settlement/pause").then((r) => r.data);
export const adminResumeSettlement = () =>
  api.post("/admin/settlement/resume").then((r) => r.data);
export const adminRunSettlementOnce = () =>
  api.post("/admin/settlement/run-once").then((r) => r.data);
export const getPartnerCampaigns = (params = {}) =>
  api.get("/partner/campaigns", { params }).then((r) => r.data);
export const getPartnerCampaign = (slug) =>
  api.get(`/partner/campaigns/${encodeURIComponent(slug)}`).then((r) => r.data);
export const getPartnerCampaignAnalytics = (slug) =>
  api.get(`/partner/campaigns/${encodeURIComponent(slug)}/analytics`).then((r) => r.data);

export const getRewardProof = (allocationId) =>
  api.get(`/rewards/${encodeURIComponent(allocationId)}/proof`).then((r) => r.data);
export const getSettlementGatesV6 = () =>
  api.get("/settlement/gates/v6").then((r) => r.data);
export const getDistributionTransparency = (id) =>
  api.get(`/distributions/${encodeURIComponent(id)}/transparency`).then((r) => r.data);
export const getPartnerCampaignReport = (slug, format = "json") =>
  api.get(`/partner/campaigns/${encodeURIComponent(slug)}/report`, { params: { format } }).then((r) => r.data);

export const getSettlementGatesV7 = () =>
  api.get("/settlement/gates/v7").then((r) => r.data);

export const getSettlementGatesV8 = () =>
  api.get("/settlement/gates/v8").then((r) => r.data);

export const getLivePilotReadiness = (distributionId) =>
  api.get(`/admin/reward-distributions/${encodeURIComponent(distributionId)}/live-pilot-readiness`).then((r) => r.data);

export const getDistributionHealth = (distributionId) =>
  api.get(`/admin/reward-distributions/${encodeURIComponent(distributionId)}/health`).then((r) => r.data);

export const getLivePilotCommandCenter = () =>
  api.get("/admin/live-pilot/command-center").then((r) => r.data);

export const getFirstLivePilotConfig = () =>
  api.get("/admin/live-pilot/first/config").then((r) => r.data);

export const getFirstLivePilotReadiness = () =>
  api.get("/admin/live-pilot/first/readiness").then((r) => r.data);

export const getFirstLivePilotBlockers = () =>
  api.get("/admin/live-pilot/first/blockers").then((r) => r.data);

export const getFirstLivePilotStatus = () =>
  api.get("/admin/live-pilot/first/status").then((r) => r.data);

export const getFirstLivePilotChecklist = () =>
  api.get("/admin/live-pilot/first/checklist").then((r) => r.data);

export const getFirstLivePilotActivationPreview = () =>
  api.get("/admin/live-pilot/first/activation-preview").then((r) => r.data);

export const postFirstLivePilotActivateDryRun = () =>
  api.post("/admin/live-pilot/first/activate", { dry_run: true }).then((r) => r.data);

export const getSettlementGatesV9 = () =>
  api.get("/settlement/gates/v9").then((r) => r.data);

export const getLiveRewardPilot = (pilotId) =>
  api.get(`/admin/live-reward-pilots/${encodeURIComponent(pilotId)}`).then((r) => r.data);

export const getLiveRewardPilotCommandCenter = (pilotId) =>
  api.get(`/admin/live-reward-pilots/${encodeURIComponent(pilotId)}/command-center`).then((r) => r.data);

export const getPartnerLiveRewardPilot = (pilotId) =>
  api.get(`/partner/live-reward-pilots/${encodeURIComponent(pilotId)}`).then((r) => r.data);

export const getPublicPilotTransparency = (pilotId) =>
  api.get(`/public/live-reward-pilots/${encodeURIComponent(pilotId)}/transparency`).then((r) => r.data);
export const getPilotReadiness = (distributionId) =>
  api.get(`/admin/reward-distributions/${encodeURIComponent(distributionId)}/pilot-readiness`).then((r) => r.data);
export const getPilotDashboard = () =>
  api.get("/admin/pilot/dashboard").then((r) => r.data);
export const getPartnerBillingPreview = (slug) =>
  api.get(`/partner/campaigns/${encodeURIComponent(slug)}/billing-preview`).then((r) => r.data);
